/**
 * Сборка CSV, который Excel открывает двойным щелчком и не портит.
 *
 * Модуль намеренно ничего не знает ни про отчёты, ни про переводы: сюда
 * приходят уже готовые строки, отсюда уходит текст файла. Всё, что он решает, —
 * три вопроса, на которых выгрузки ломаются чаще всего.
 *
 * Первый — кодировка. Excel в Windows читает CSV в системной кодировке, пока не
 * увидит в начале метку UTF-8; без неё кириллица и умляуты превращаются в
 * мусор.
 *
 * Второй — разделитель. Он не свойство формата, а свойство языка: там, где
 * запятая занята под дробную часть (немецкий, русский, румынский), Excel ждёт
 * точку с запятой, и наоборот.
 *
 * Третий — чужой текст. Имена машин и адреса приходят извне, а Excel выполняет
 * ячейку, начинающуюся со знака равенства, плюса, собаки или минуса. Это
 * известный способ увести данные из чужой таблицы, и здесь он закрыт.
 */

/** Как выглядит CSV для конкретного языка интерфейса. */
export type ExcelLocale = {
  /** Разделитель колонок. */
  delimiter: string
  /** Десятичный разделитель — им же форматируются числа в ячейках. */
  decimal: string
}

const COMMA_DECIMAL: ExcelLocale = { delimiter: ';', decimal: ',' }
const DOT_DECIMAL: ExcelLocale = { delimiter: ',', decimal: '.' }

/** Раскладка по языкам интерфейса. Ключи совпадают с LANGUAGES из i18n. */
export const EXCEL_LOCALE = {
  de: COMMA_DECIMAL,
  'ro-MD': COMMA_DECIMAL,
  ru: COMMA_DECIMAL,
  en: DOT_DECIMAL,
} as const satisfies Record<string, ExcelLocale>

/** Метка порядка байтов: по ней Excel понимает, что файл в UTF-8. */
const BOM = '﻿'

/** Знаки, с которых Excel начинает считать содержимое ячейки формулой. */
const FORMULA_START = /^[=+@\t\r]/

/** Число целиком: «12», «-12», «12,5», «-12.5». */
const PLAIN_NUMBER = /^-?\d+(?:[.,]\d+)?$/

/**
 * Превращает опасную ячейку обратно в текст.
 *
 * Минус разбирается отдельно: он открывает формулу, но с него же начинается
 * любое отрицательное число. Апостроф перед «-12,5» сделал бы из измеренной
 * величины строку, и сумма в Excel перестала бы считаться, — поэтому минус
 * обезвреживается только там, где за ним стоит не число.
 */
function neutralize(value: string): string {
  if (FORMULA_START.test(value)) return `'${value}`
  if (value.startsWith('-') && !PLAIN_NUMBER.test(value)) return `'${value}`
  return value
}

/** Одна ячейка: обезврежена и, если нужно, взята в кавычки. */
function cell(value: string, delimiter: string): string {
  const safe = neutralize(value)
  const needsQuotes =
    safe.includes(delimiter) || safe.includes('"') || safe.includes('\n') || safe.includes('\r')
  return needsQuotes ? `"${safe.replace(/"/g, '""')}"` : safe
}

/**
 * Собирает текст CSV-файла.
 *
 * Переводы строк — CRLF: так требует RFC 4180, и так файл открывается в старых
 * версиях Excel, которые одинокий перевод строки не считают концом записи.
 */
export function toCsv(header: string[], rows: string[][], locale: ExcelLocale): string {
  const line = (values: string[]) =>
    values.map((value) => cell(value, locale.delimiter)).join(locale.delimiter)
  return BOM + [header, ...rows].map(line).join('\r\n') + '\r\n'
}
